import React, { useState } from 'react';
import { X, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEEDBACK_CATEGORIES = [
  { value: 'WRONG_CONTEXT', label: 'Feil kontekst' },
  { value: 'MISSING_POINTS', label: 'Mangler viktige punkter' },
  { value: 'TOO_GENERAL', label: 'For generelt' },
  { value: 'TOO_DETAILED', label: 'For detaljert' },
  { value: 'WRONG_FORMAT', label: 'Feil format' },
  { value: 'HALLUCINATION', label: 'Gir hallucinasjoner' },
  { value: 'UNCLEAR', label: 'Utydelig' },
  { value: 'OTHER', label: 'Annet' }
];

const FeedbackModal = ({ agentRunId, suggestionId, onClose, onSubmit }) => {
  const [correctness, setCorrectness] = useState(null);
  const [categories, setCategories] = useState([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleCategoryToggle = (category) => {
    if (categories.includes(category)) {
      setCategories(categories.filter(c => c !== category));
    } else {
      setCategories([...categories, category]);
    }
  };

  const handleSubmit = async () => {
    if (!correctness) {
      toast.error('Vennligst velg om dette stemmer eller ikke');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/agents/feedback`, {
        agent_run_id: agentRunId,
        suggestion_id: suggestionId,
        correctness: correctness,
        categories: categories,
        comment: comment || null
      });

      toast.success('Takk for tilbakemeldingen!', {
        description: 'Dette hjelper oss å forbedre agentene.'
      });

      if (onSubmit) onSubmit();
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Kunne ikke sende tilbakemelding');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Gi tilbakemelding</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Correctness */}
          <div>
            <label className="text-sm font-medium text-gray-900 mb-2 block">
              Stemmer dette?
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setCorrectness('YES')}
                className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                  correctness === 'YES'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <CheckCircle className={`w-5 h-5 mx-auto mb-1 ${
                  correctness === 'YES' ? 'text-green-600' : 'text-gray-400'
                }`} />
                <div className="text-sm font-medium text-gray-900">Ja</div>
              </button>

              <button
                onClick={() => setCorrectness('PARTIAL')}
                className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                  correctness === 'PARTIAL'
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-gray-200 hover:border-amber-300'
                }`}
              >
                <AlertCircle className={`w-5 h-5 mx-auto mb-1 ${
                  correctness === 'PARTIAL' ? 'text-amber-600' : 'text-gray-400'
                }`} />
                <div className="text-sm font-medium text-gray-900">Delvis</div>
              </button>

              <button
                onClick={() => setCorrectness('NO')}
                className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                  correctness === 'NO'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-red-300'
                }`}
              >
                <XCircle className={`w-5 h-5 mx-auto mb-1 ${
                  correctness === 'NO' ? 'text-red-600' : 'text-gray-400'
                }`} />
                <div className="text-sm font-medium text-gray-900">Nei</div>
              </button>
            </div>
          </div>

          {/* Categories (only if not YES) */}
          {correctness && correctness !== 'YES' && (
            <div>
              <label className="text-sm font-medium text-gray-900 mb-2 block">
                Hva er feil? (valgfritt)
              </label>
              <div className="space-y-2">
                {FEEDBACK_CATEGORIES.map(cat => (
                  <label key={cat.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={categories.includes(cat.value)}
                      onChange={() => handleCategoryToggle(cat.value)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-gray-500"
                    />
                    <span className="text-sm text-gray-700">{cat.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Comment */}
          <div>
            <label className="text-sm font-medium text-gray-900 mb-2 block">
              Kommentar (valgfritt)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder='F.eks. "Burde vist til avsnitt X", "Dette mangler", "Dette er feil"'
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent resize-none"
              rows={3}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-gray-200">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={submitting}
          >
            Avbryt
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            disabled={!correctness || submitting}
          >
            {submitting ? 'Sender...' : 'Send tilbakemelding'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
